import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const AddressInformationSection = ({ member, isEditing, formData, onFormChange, errors }) => {
  const addressTypeOptions = [
    { value: 'home', label: 'Home' },
    { value: 'work', label: 'Work' },
    { value: 'mailing', label: 'Mailing' },
    { value: 'other', label: 'Other' }
  ];

  const stateOptions = [
    { value: 'AC', label: 'Acre' },
    { value: 'AL', label: 'Alagoas' },
    { value: 'AP', label: 'Amapá' },
    { value: 'AM', label: 'Amazonas' },
    { value: 'BA', label: 'Bahia' },
    { value: 'CE', label: 'Ceará' },
    { value: 'DF', label: 'Distrito Federal' },
    { value: 'ES', label: 'Espírito Santo' },
    { value: 'GO', label: 'Goiás' },
    { value: 'MA', label: 'Maranhão' },
    { value: 'MT', label: 'Mato Grosso' },
    { value: 'MS', label: 'Mato Grosso do Sul' },
    { value: 'MG', label: 'Minas Gerais' },
    { value: 'PA', label: 'Pará' },
    { value: 'PB', label: 'Paraíba' },
    { value: 'PR', label: 'Paraná' },
    { value: 'PE', label: 'Pernambuco' },
    { value: 'PI', label: 'Piauí' },
    { value: 'RJ', label: 'Rio de Janeiro' },
    { value: 'RN', label: 'Rio Grande do Norte' },
    { value: 'RS', label: 'Rio Grande do Sul' },
    { value: 'RO', label: 'Rondônia' },
    { value: 'RR', label: 'Roraima' },
    { value: 'SC', label: 'Santa Catarina' },
    { value: 'SP', label: 'São Paulo' },
    { value: 'SE', label: 'Sergipe' },
    { value: 'TO', label: 'Tocantins' }
  ];

  const addAddress = () => {
    const newAddresses = [...formData?.addresses, {
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      zipCode: '',
      type: 'home',
      isPrimary: false
    }];
    onFormChange('addresses', newAddresses);
  };

  const removeAddress = (index) => {
    const newAddresses = formData?.addresses?.filter((_, i) => i !== index);
    onFormChange('addresses', newAddresses);
  };

  const updateAddress = (index, field, value) => {
    const newAddresses = [...formData?.addresses];
    newAddresses[index][field] = value;
    
    // If setting as primary, unset others
    if (field === 'isPrimary' && value) {
      newAddresses?.forEach((address, i) => {
        if (i !== index) address.isPrimary = false;
      });
    }
    
    onFormChange('addresses', newAddresses);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 mb-6">
      <div className="flex items-center space-x-2 mb-4">
        <Icon name="MapPin" size={20} className="text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Address Information</h2>
      </div>
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-md font-medium text-foreground">Addresses</h3>
          {isEditing && (
            <Button
              variant="outline"
              size="sm"
              onClick={addAddress}
              iconName="Plus"
              iconPosition="left"
            >
              Add Address
            </Button>
          )}
        </div>

        <div className="space-y-6">
          {(isEditing ? formData?.addresses : member?.addresses)?.map((address, index) => (
            <div key={index} className="p-4 bg-muted rounded-lg border border-border">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Icon name="Home" size={16} className="text-muted-foreground" />
                  <span className="font-medium text-foreground capitalize">
                    {address?.type} Address
                  </span>
                  {address?.isPrimary && (
                    <span className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded">
                      Primary
                    </span>
                  )}
                </div>
                {isEditing && formData?.addresses?.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeAddress(index)}
                    iconName="Trash2"
                    className="text-error hover:text-error"
                  />
                )}
              </div>

              {isEditing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Select
                      label="Address Type"
                      options={addressTypeOptions}
                      value={address?.type}
                      onChange={(value) => updateAddress(index, 'type', value)}
                    />
                    <div className="flex items-center space-x-2 pt-6">
                      <input
                        type="checkbox"
                        checked={address?.isPrimary}
                        onChange={(e) => updateAddress(index, 'isPrimary', e?.target?.checked)}
                        className="rounded border-border"
                      />
                      <label className="text-sm font-medium">Set as primary address</label>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <Input
                        label="Street"
                        type="text"
                        value={address?.street}
                        onChange={(e) => updateAddress(index, 'street', e?.target?.value)}
                        placeholder="Enter street name"
                        error={errors?.addresses && errors?.addresses?.[index] && errors?.addresses?.[index]?.street}
                        required
                      />
                    </div>
                    <Input
                      label="Number"
                      type="text"
                      value={address?.number}
                      onChange={(e) => updateAddress(index, 'number', e?.target?.value)}
                      placeholder="123"
                      error={errors?.addresses && errors?.addresses?.[index] && errors?.addresses?.[index]?.number}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Complement"
                      type="text"
                      value={address?.complement}
                      onChange={(e) => updateAddress(index, 'complement', e?.target?.value)}
                      placeholder="Apt, Suite, etc."
                    />
                    <Input
                      label="Neighborhood"
                      type="text"
                      value={address?.neighborhood}
                      onChange={(e) => updateAddress(index, 'neighborhood', e?.target?.value)}
                      placeholder="Enter neighborhood"
                      error={errors?.addresses && errors?.addresses?.[index] && errors?.addresses?.[index]?.neighborhood}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="City"
                      type="text"
                      value={address?.city}
                      onChange={(e) => updateAddress(index, 'city', e?.target?.value)}
                      placeholder="Enter city"
                      error={errors?.addresses && errors?.addresses?.[index] && errors?.addresses?.[index]?.city}
                      required
                    />
                    <Select
                      label="State"
                      options={stateOptions}
                      value={address?.state}
                      onChange={(value) => updateAddress(index, 'state', value)}
                      error={errors?.addresses && errors?.addresses?.[index] && errors?.addresses?.[index]?.state}
                      searchable
                    />
                    <Input
                      label="ZIP Code"
                      type="text"
                      value={address?.zipCode}
                      onChange={(e) => updateAddress(index, 'zipCode', e?.target?.value)}
                      placeholder="00000-000"
                      error={errors?.addresses && errors?.addresses?.[index] && errors?.addresses?.[index]?.zipCode}
                      required
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-foreground">
                    {address?.street}, {address?.number}
                    {address?.complement && `, ${address?.complement}`}
                  </p>
                  <p className="text-foreground">
                    {address?.neighborhood}, {address?.city} - {address?.state}
                  </p>
                  <p className="text-foreground">{address?.zipCode}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AddressInformationSection;